import React, { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { cn } from '@/lib/utils'
import type { RateHistoryEntry } from '@/types/calendar'

interface RateHistoryChartProps {
  history: RateHistoryEntry[]
  currency: string
}

// Clearance carries the amber it wears on every other surface (the calendar badge, the day
// popup) and its own dot colour, because this chart is where the origin incident happened: a
// stepped-down price that looks like an ordinary computation explains nothing.
//
// THE ENDING IS NEUTRAL, AND IT IS NOT AMBER (operator ruling, 2026-09-02). `clearance_ended` is
// the rule letting a price go back to normal, so it is a rise: amber would put the "the engine
// cut this price" colour on a point going up, and teal would read as a good outcome the module
// never claims (§12 rule 6 — a withdrawal is a measurement, not a win). Slate is the honest one.
const SOURCE_COLORS: Record<string, string> = {
  computation: '#0d9488',
  clearance: '#d97706',
  clearance_ended: '#64748b',
  manual_override: '#f59e0b',
  rule_change: '#6366f1',
  bulk_edit: '#f59e0b',
  drift_detected: '#ef4444',
}

export const SOURCE_LABELS: Record<string, string> = {
  computation: 'Computed',
  clearance: 'Clearance',
  clearance_ended: 'Clearance ended',
  manual_override: 'Override',
  rule_change: 'Rule change',
  bulk_edit: 'Bulk edit',
  drift_detected: 'Drift',
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

const HistoryTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0]?.payload
  if (!d) return null
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-muted-foreground">{d.label}</p>
      <p className="text-sm font-semibold tabular-nums text-foreground">{d.sym}{d.amount.toFixed(0)}</p>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ backgroundColor: SOURCE_COLORS[d.source] || '#94a3b8' }}
        />
        <span className="text-[11px] text-muted-foreground">{SOURCE_LABELS[d.source] || d.source}</span>
      </div>
    </div>
  )
}

export default function RateHistoryChart({ history, currency }: RateHistoryChartProps) {
  const sym = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency

  const data = useMemo(() => {
    if (!history?.length) return []
    // History comes latest-first, reverse for chronological order
    return [...history].reverse().map((h) => ({
      label: formatDateTime(h.at),
      amount: h.amount,
      source: h.source,
      sym,
    }))
  }, [history, sym])

  if (data.length < 2) {
    return (
      <div className="flex items-center justify-center h-[120px] text-xs text-muted-foreground">
        {data.length === 1
          ? `Set to ${sym}${data[0].amount.toFixed(0)} (${SOURCE_LABELS[data[0].source] || data[0].source})`
          : 'No rate history yet'}
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={120}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 9, fill: '#64748b' }}
          tickLine={false}
          axisLine={{ stroke: '#e2e8f0' }}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 10, fill: '#64748b' }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: number) => `${sym}${v}`}
          width={45}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<HistoryTooltip />} />
        <Line
          type="stepAfter"
          dataKey="amount"
          stroke="#0d9488"
          strokeWidth={2}
          dot={(props: any) => {
            const { cx, cy, payload } = props
            const color = SOURCE_COLORS[payload.source] || '#94a3b8'
            return <circle key={cx} cx={cx} cy={cy} r={4} fill={color} stroke="white" strokeWidth={1.5} />
          }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}
